import React, { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { LightThemeTableWithPagination } from "components/LightThemeTableWithPagination";
import { State } from "state";
import { getProjectStores, updateStoreDeadline, deleteProjectStores, getProjectStoreMappingComparison, } from "state/project_store_deadline";
import { ComponentState, OwnProps } from "interfaces/project";
import WarningIcon from "assets/img/WarningIcon";

// CSS Constants 
const checkboxCSS = `
  .custom-checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  .custom-checkbox {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    width: 0;
    height: 0;
  }
  .custom-checkmark {
    display: inline-block;
    width: 15px;
    height: 15px;
    border: 2px solid #E5B611;
    border-radius: 2px;
    transition: all 0.2s ease;
    background: #fff;
  }
  .custom-checkbox:checked + .custom-checkmark {
    background-color: #E5B611;
    border-color: #E5B611;
  }
  .custom-checkbox:checked + .custom-checkmark::after {
    content: "";
    position: absolute;
    width: 3px;
    height: 6px;
    border: solid white;
    border-width: 0 1.5px 1.5px 0;
    transform: translate(-50%, -60%) rotate(45deg);
    left: 50%;
    top: 50%;
  }
`;

const cellStyle: React.CSSProperties = {
  paddingTop: 8,
  paddingBottom: 8,
};

const dateInputStyle: React.CSSProperties = {
  padding: "4px 6px",
  borderRadius: 2,
  border: "1px solid #ccc",
  fontSize: 13,
  width: 200,
};

class Step3Form extends Component<PropsFromRedux & OwnProps, ComponentState> {
  state: ComponentState = {
    stores: [],
    currentPage: 1,
    limit: 100,
    selectedRows: [],
    selectAll: false,
    bulkDate: "",
    isDeleting: false,
    showDeleteModal: false,
    storesToDelete: [],
  };

  componentDidMount() {
    const { projectId } = this.props;
    if (projectId) { this.fetchStores(); this.fetchMappingComparsion(); }
  }

  componentDidUpdate(prevProps: PropsFromRedux & OwnProps) {
    if (prevProps.stores !== this.props.stores) {
      const newStores = this.props.stores ?? [];
      this.setState({ stores: newStores }, () => {
        if (newStores.length === 0 && this.state.currentPage > 1) {
          this.setState(
            (prev) => ({ currentPage: prev.currentPage - 1 }),
            this.fetchStores
          );
        }
      });
    }
  }

  fetchStores = () => {
    const { projectId, getProjectStores } = this.props;
    const { currentPage, limit } = this.state;
    if (projectId) getProjectStores(projectId, currentPage, limit);
  };

  fetchMappingComparsion = () => {
    const { projectId, getProjectStoreMappingComparison } = this.props;
    if (projectId) getProjectStoreMappingComparison(projectId);
  };

  formatDateForInput = (dateString: string = ""): string => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  };

  handleDateChange = async (id: number, newDate: string) => {
    const { projectId, updateStoreDeadline } = this.props;
    if (!projectId) return;

    this.setState((prev) => ({
      stores: prev.stores.map((s) =>
        s.id === id ? { ...s, validation_deadline: newDate } : s
      ),
    }));

    try {
      await updateStoreDeadline(projectId, [{ id, validation_deadline: newDate }]);
      this.fetchStores();
      this.setState((prev) => ({
        selectedRows: prev.selectedRows.filter((rowId) => rowId !== id),
        selectAll: false,
      }));
    } catch (err) {
      console.error("Failed to update deadline:", err);
    }
  };

  handleBulkDateChange = async (newDate: string) => {
    const { projectId, updateStoreDeadline } = this.props;
    const { selectedRows } = this.state;
    if (!projectId || selectedRows.length === 0) return;

    const payload = selectedRows.map((id) => ({
      id,
      validation_deadline: newDate,
    }));

    try {
      await updateStoreDeadline(projectId, payload);
      this.fetchStores();
      this.setState({ selectedRows: [], selectAll: false, bulkDate: newDate });
    } catch (err) {
      console.error("Failed to update bulk deadlines:", err);
    }
  };

  handlePageChange = (page: number, newLimit: number) => {
    this.setState({ currentPage: page, limit: newLimit }, this.fetchStores);
  };

  handleSelectAll = () => {
    this.setState((prev) => ({
      selectAll: !prev.selectAll,
      selectedRows: !prev.selectAll ? prev.stores.map((s) => s.id) : [],
    }));
  };

  handleRowSelect = (id: number) => {
    this.setState((prev) => {
      const isSelected = prev.selectedRows.includes(id);
      const selectedRows = isSelected
        ? prev.selectedRows.filter((i) => i !== id)
        : [...prev.selectedRows, id];
      return {
        selectedRows,
        selectAll: selectedRows.length === prev.stores.length,
      };
    });
  };

  // Delete handler for both single and bulk delete
  handleDelete = (mappedId?: number, project_id?: any, store_id?: any) => {
    const { stores, selectedRows } = this.state;
    
    let storesToDelete;
    
    // If specific IDs are passed, it's a single delete
    if (mappedId !== undefined && project_id !== undefined && store_id !== undefined) {
      storesToDelete = [{
        id: mappedId,
        store_id: store_id,
        project_id: project_id
      }];
    } 
    // Otherwise, it's a bulk delete using selected rows
    else {
      if (selectedRows.length === 0) {
        alert("Please select at least one store to delete");
        return;
      }
      
      storesToDelete = stores
        .filter(store => selectedRows.includes(store.id))
        .map(store => ({
          id: store.id,
          store_id: store.store_id,
          project_id: store.project_id
        }));
    }

    // Show modal with stores to delete
    this.setState({
      showDeleteModal: true,
      storesToDelete: storesToDelete,
    });
  };

  handleDeleteConfirmed = async () => {
    const { projectId, deleteProjectStores } = this.props;
    const { storesToDelete } = this.state;
    
    if (!projectId) return;

    this.setState({ isDeleting: true });

    try {
      // Call the delete API with array of stores (works for both single and bulk)
      await deleteProjectStores(projectId, storesToDelete);
      
      // Refresh the store list after successful deletion
      this.fetchStores();
      this.fetchMappingComparsion();
      
      // Clear all selections and close modal
      this.setState({
        selectedRows: [],
        selectAll: false,
        isDeleting: false,
        showDeleteModal: false,
        storesToDelete: [],
      });
    } catch (err) {
      console.error("Failed to delete stores:", err);
      this.setState({ isDeleting: false });
    }
  };

  render() {
    const { stores, selectedRows, selectAll, bulkDate, isDeleting, showDeleteModal, storesToDelete, currentPage, limit } = this.state;
    const { project, loadingProjectStores, totalRecords = 0, mappingComparsion, mappingComparsionLoading, } = this.props;
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
    const projectExpiry = this.formatDateForInput(project?.expires_at);
    const projectCreated = this.formatDateForInput(project?.created_at);
    const columns = [
      {
        field: "checkbox",
        header: (
          <label className="custom-checkbox-wrapper">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={this.handleSelectAll}
              className="custom-checkbox"
            />
            <span className="custom-checkmark"></span>
          </label>
        ),
        formatter: (_: any, row: any) => (
          <label className="custom-checkbox-wrapper">
            <input
              type="checkbox"
              checked={selectedRows.includes(row.id)}
              onChange={() => this.handleRowSelect(row.id)}
              className="custom-checkbox"
            />
            <span className="custom-checkmark"></span>
          </label>
        ),
        width: "50px",
      },
      {
        field: "storeNumber",
        header: "Store No",
        formatter: (_: any, row: any) => <div style={cellStyle}>{row.storeNumber}</div>,
      },
      {
        field: "deadline",
        header: "Validation Deadline",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>
            <input
              type="date"
              value={this.formatDateForInput(row?.validation_deadline ? row?.validation_deadline : project?.expires_at)}
              onChange={(e) => this.handleDateChange(row.id, e.target.value)}
              max={projectExpiry}
              min={projectCreated}
              style={dateInputStyle}
            />
          </div>
        ),
      },
      {
        field: "address",
        header: "Address",
        formatter: (_: any, row: any) => <div style={cellStyle}>{row.address}</div>,
      },
      {
        field: "storeState",
        header: "Store State",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row.state ?? "-"}</div>
        ),
      },
      {
        field: "action",
        header: "Action",
        width: "60px",
        formatter: (_: any, row: any) => (
          <>
            <img
              src={require("../../assets/img/Delete.png")}
              alt="delete"
              style={{
                cursor: "pointer",
                width: 22,
                height: 22,
                padding: 4,
              }}
              onClick={() => this.handleDelete(row.id, row.project_id, row.store_id)}
            />
          </>
        ),
      },
    ];

    return (
      <div>
        <style>{checkboxCSS}</style>
        {mappingComparsion?.hasMissingMappings && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 8,
              backgroundColor: "#FFF8E7",
              border: "1px solid #E5B42F",
              color: "#6E5209",
              fontSize: 13,
              lineHeight: 1.55,
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginBottom: 8,
            }}
          >
            <WarningIcon />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontWeight: 600, color: "#5D4307" }}>
                Mapping mismatch detected
              </span>
              <span>
                These stores are missing in store to project mapping but have Planogram or PDF Files in store to file mapping. Please re-upload the store mapping file in Step 2.
              </span>
              {mappingComparsion.missingStoreNumbers?.length > 0 && (
                <div style={{ marginTop: 2 }}>
                  <span style={{ fontWeight: 600 }}>Store numbers:</span>{" "}
                  {mappingComparsion.missingStoreNumbers.join(", ")}
                </div>
              )}
            </div>
          </div>
        )}
        {mappingComparsionLoading && (
          <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
            Checking store mapping integrity...
          </div>
        )}
        {/* Bulk Date Selector */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: 12,
            gap: 6,
          }}
        >
          <label style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
            Change validation deadline dates
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <input
              type="date"
              value={bulkDate}
              max={projectExpiry}
              min={projectCreated}
              onChange={(e) => this.handleBulkDateChange(e.target.value)}
              style={{
                ...dateInputStyle,
                width: 160,
                borderRadius: 4,
              }}
            />
            <span
              style={{
                fontSize: 10,
                backgroundColor: "#E3AF320D",
                border: "1px solid #E5B42F42",
                padding: "3px 6px",
                borderRadius: 4,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid #FB0707",
                  color: "red",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                !
              </span>
              <span style={{ fontStyle: "italic" }}>
                Change deadline dates for all selected stores
              </span>
            </span>
          </div>
          
          {/* Bulk Delete Button */}
          {selectedRows.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%", marginTop: 4 }}>
              <button
                onClick={() => this.handleDelete()}
                disabled={isDeleting}
                style={{
                  padding: "6px 12px",
                  backgroundColor: isDeleting ? "#cccccc" : "#E5B611",
                  color: "#000",
                  border: "none",
                  borderRadius: 4,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: isDeleting ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = "#d4a510";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeleting) {
                    e.currentTarget.style.backgroundColor = "#E5B611";
                  }
                }}
              >
                {isDeleting ? "Deleting..." : `Delete Selected (${selectedRows.length})`}
              </button>
            </div>
          )}
        </div>

        <LightThemeTableWithPagination
          data={stores}
          columns={columns}
          currentPage={currentPage}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={this.handlePageChange}
          pageSizeOptions={[2, 5, 10, 100, 200]}
          isLoading={loadingProjectStores}
        />
        {showDeleteModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() =>
              this.setState({
                showDeleteModal: false,
                storesToDelete: [],
              })
            }
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "450px",
                width: "90%",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                textAlign: "left",
                color: "black",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginBottom: "16px", fontSize: "18px", color: "black", fontWeight: 600 }}>
                Are you sure?
              </h3>

              <p style={{ marginBottom: "24px", fontSize: "14px", color: "#666" }}>
                Do you really want to delete selected store(s) project mapping? This action cannot be undone.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                  marginTop: "20px",
                }}
              >
                <button
                  onClick={() =>
                    this.setState({
                      showDeleteModal: false,
                      storesToDelete: [],
                    })
                  }
                  disabled={isDeleting}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#E5B611",
                    border: "1px solid #E5B611",
                    borderRadius: "6px",
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    color: "#000",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={this.handleDeleteConfirmed}
                  disabled={isDeleting}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: isDeleting ? "#E5B611" : "#E5B611",
                    border: "none",
                    borderRadius: "6px",
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    color: "#000",
                    fontWeight: 500,
                    opacity: isDeleting ? 0.7 : 1,
                  }}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

// Redux Connection 
const connector = connect(
  (state: State) => ({
    stores: state.projectStoresDeadline?.content ?? [],
    totalRecords: state.projectStoresDeadline?.totalRecords ?? 0,
    loadingProjectStores: state.projectStoresDeadline?.loading,
    mappingComparsion: state.projectStoresDeadline?.mappingComparsion,
    mappingComparsionLoading: state.projectStoresDeadline?.mappingComparsionLoading,
  }),
  { getProjectStores, updateStoreDeadline, deleteProjectStores, getProjectStoreMappingComparison }
);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(Step3Form);